import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./RequiredMods.css";

function installedIds(config) {
  if (!config?.game || (config.game.mods !== undefined && !Array.isArray(config.game.mods))) {
    throw new Error("Invalid configuration");
  }
  return new Set((config.game.mods ?? []).map(mod => String(mod.modId || "").toUpperCase()));
}

export default function RequiredMods({ workshopId, onImported, onCancel }) {
  const [required, setRequired] = useState([]);
  const [installed, setInstalled] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [retry, setRetry] = useState(0);
  const active = useRef(false);
  const importLock = useRef(false);

  useEffect(() => {
    let current = true;
    active.current = true;
    setLoading(true);
    setRequired([]);
    setFeedback(null);
    Promise.all([
      axios.get(`/api/workshop/item/${encodeURIComponent(workshopId)}/dependencies`),
      axios.get("/api/config")
    ]).then(([dependencies, configuration]) => {
      if (!Array.isArray(dependencies.data)) throw new Error("Invalid dependencies");
      const ids = installedIds(configuration.data);
      const unique = new Map();
      for (const mod of dependencies.data) {
        if (!mod?.workshopId) throw new Error("Invalid dependency");
        unique.set(mod.workshopId.toUpperCase(), mod);
      }
      if (current) { setRequired([...unique.values()]); setInstalled(ids); }
    }).catch(() => {
      if (current) setFeedback({ error: true, message: "Required mods could not be loaded. Please try again." });
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; active.current = false; };
  }, [workshopId, retry]);

  const missing = required.filter(mod => !installed.has(mod.workshopId.toUpperCase()));
  const importMissing = async () => {
    if (importLock.current || !missing.length) return;
    importLock.current = true;
    setImporting(true);
    setFeedback(null);
    try {
      const { data } = await axios.post(`/api/workshop/item/${encodeURIComponent(workshopId)}/import-mods`);
      const ids = installedIds(data.config);
      onImported?.(data.config, data.importedCount);
      if (active.current) {
        setInstalled(ids);
        setFeedback({ message: data.importedCount
          ? `${data.importedCount} missing mod${data.importedCount === 1 ? "" : "s"} imported.` : "All required mods are already installed." });
      }
    } catch {
      if (active.current) setFeedback({ error: true, message: "Required mods could not be imported. Please try again." });
    } finally {
      importLock.current = false;
      if (active.current) setImporting(false);
    }
  };

  return (
    <section className="required-mods" aria-labelledby="required-mods-heading">
      <h4 id="required-mods-heading">Required Mods</h4>
      <p className="muted">The selected Workshop mod and its dependencies.</p>
      {loading && <p role="status">Loading required mods…</p>}
      {feedback && <p role={feedback.error ? "alert" : "status"} className={feedback.error ? "workshop-error" : "muted"}>{feedback.message}</p>}
      {!loading && !required.length && feedback?.error && <button type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>}
      {!loading && required.length > 0 && <ul className="required-mods-list">
        {required.map(mod => <li key={mod.workshopId} aria-label={mod.name}>
          {mod.thumbnail ? <img src={mod.thumbnail} alt={mod.name || "Workshop thumbnail"} loading="lazy" />
            : <div className="required-mod-thumbnail">No thumbnail</div>}
          <div>
            <strong>{mod.name}</strong>
            <p>By {mod.author || "Unknown author"}</p>
            <p className="required-mod-id">{mod.workshopId}</p>
            <p className={installed.has(mod.workshopId.toUpperCase()) ? "required-mod-installed" : "muted"}>
              {installed.has(mod.workshopId.toUpperCase()) ? "✓ Already Installed" : "Not Installed"}
            </p>
          </div>
        </li>)}
      </ul>}
      <div className="workshop-card-actions">
        <button type="button" disabled={loading || importing || !missing.length} onClick={importMissing}>Import Missing Mods</button>
        <button type="button" className="button-secondary" disabled={importing} onClick={onCancel}>Cancel</button>
      </div>
      {importing && <p role="status">Importing missing mods…</p>}
    </section>
  );
}
